import React, { createContext, useState, useCallback, useEffect, useRef, useContext } from 'react';

/** Verifica se a aplicação está rodando em rede local (localhost, LAN ou host do /etc/hosts). */
export function isLocalNetwork() {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname;
  return (
    h === 'localhost' ||
    h === '127.0.0.1' ||
    h === 'pcpcryptotransfer.test' ||
    /^192\.168\./.test(h) ||
    /^10\./.test(h)
  );
}

const MAX_LOGS = 500;

export const DevLogContext = createContext();

export function DevLogProvider({ children }) {
  const [logs, setLogs] = useState([]);
  const idRef = useRef(0);
  const isActive = true;

  const addLog = useCallback((level, message, data) => {
    const id = ++idRef.current;
    const entry = {
      id,
      ts: new Date().toISOString(),
      level,
      message: typeof message === 'string' ? message : String(message),
      data: data !== undefined ? data : null,
    };
    setLogs((prev) => {
      const next = [...prev, entry];
      return next.length > MAX_LOGS ? next.slice(-MAX_LOGS) : next;
    });
  }, []);

  const clearLogs = useCallback(() => setLogs([]), []);

  // Em rede local, expõe devLog no console e registra log inicial
  useEffect(() => {
    if (typeof window === 'undefined' || !isActive) return;
    addLog('log', 'DevLog ativo. No console use: __devLog.log("msg", dados)', null);
    window.__devLog = {
      log: (msg, data) => addLog('log', msg, data),
      warn: (msg, data) => addLog('warn', msg, data),
      error: (msg, data) => addLog('error', msg, data),
    };
    return () => { delete window.__devLog; };
  }, [addLog, isActive]);

  const value = {
    logs,
    addLog,
    clearLogs,
    isLocal: isActive,
    isActive,
  };

  return (
    <DevLogContext.Provider value={value}>
      {children}
    </DevLogContext.Provider>
  );
}

/** Hook para adicionar logs a partir de componentes.
 *  Em produção (quando não for rede local), retorna no-ops para não impactar usuários finais.
 */
export function useDevLog() {
  const ctx = useContext(DevLogContext);
  if (!ctx || !ctx.isActive) {
    return { log: () => {}, warn: () => {}, error: () => {}, clearLogs: () => {} };
  }
  return {
    log: (msg, data) => ctx.addLog('log', msg, data),
    warn: (msg, data) => ctx.addLog('warn', msg, data),
    error: (msg, data) => ctx.addLog('error', msg, data),
    clearLogs: ctx.clearLogs,
  };
}
